import { EnumTipoOnereDTOTipoVincolo } from '@framework/api-client/api/inquiries/schemas/enumTipoOnereDTOTipoVincolo.ts';
import {
  getGetFrontRichiesteIdQueryKey,
  useGetFrontRichiesteIdOneri,
  usePostFrontRichiesteIdPagamenti,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumOnereWithDettaglioDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumOnereWithDettaglioDTOStato.ts';
import { type OnereWithDettaglioDTO } from '@framework/api-client/api/inquiries-compile/schemas/onereWithDettaglioDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

type GeneratePaymentTimeType = 'beforeSent' | 'afterSent';

/**
 * Hook per generare automaticamente i pagamenti dopo l'invio della richiesta.
 */
export function useGenerateAutomaticBurdenPayments(
  generationTiming: GeneratePaymentTimeType,
  submissionId?: string | null,
  skipPaymentGeneration?: boolean,
) {
  //
  const [isLoading, setIsLoading] = useState(true);

  useGetFrontRichiesteIdOneri(submissionId!, {
    query: {
      keepPreviousData: true,
      onSuccess: data => {
        handleCreatePayment(data);
      },
    },
  });

  const queryClient = useQueryClient();
  const { mutate: createPayment } = usePostFrontRichiesteIdPagamenti({
    mutation: {
      mutationKey: `postFrontRichiesteIdPagamenti-${submissionId}`,
      onSuccess: () => {
        if (!submissionId) {
          setIsLoading(false);
          return;
        }

        const submissionQueryKey =
          getGetFrontRichiesteIdQueryKey(submissionId)[0];
        queryClient
          .invalidateQueries({
            // Introduciamo l'opzione per invalidare tutte le query, compresa
            // quella di pagamenti
            refetchInactive: true,
            predicate: ({ queryKey }) =>
              // Questa logica ci permette di invalidare tutte le richieste che
              // vengono da uno stesso path padre
              // (e.g `/framework-cmp-domande/richieste/${string}`)
              (queryKey[0] as string).includes(submissionQueryKey),
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
      onError: error => {
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(error, t("burden-selection-table.errore-in-creazione-pagamento", 'Si è verificato un errore durante la creazione del pagamento. Riprovare.')));
        setIsLoading(false);
      },
    },
  });

  const handleCreatePayment = (burdens: OnereWithDettaglioDTO[]) => {
    if (!!skipPaymentGeneration || !submissionId) {
      setIsLoading(false);
      return;
    }

    const filteredBurdens = burdens.filter(b => {
      const shouldGenerate =
        generationTiming === 'beforeSent'
          ? !!b.tipoOnere?.flGeneraAutomaticamentePacchetto
          : b.tipoOnere?.tipoVincolo ===
            EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO;

      return (
        b.stato === EnumOnereWithDettaglioDTOStato.DA_PAGARE && shouldGenerate
      );
    });

    if (filteredBurdens.length == 0) {
      setIsLoading(false);
      return;
    }

    createPayment({
      id: submissionId,
      data: {
        // Estraggo tutti gli oneri e bolli non pagati e senza pacchetto di pagamento
        richiestaOneri: filteredBurdens.map(({ id }) => ({ id })) ?? [],
      },
    });
  };

  return isLoading;
}
